import type { ReactNode } from "react";
import type { Components } from "react-markdown";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ExternalLink } from "lucide-react";
import { normalizeAssistantMarkdown, normalizeRecommendMarkdown } from "@/lib/format";

const RecommendExternalLink = ({ href, children }: { href?: string; children?: ReactNode }) => {
	if (!href) {
		return <>{children}</>;
	}
	return (
		<a
			href={href}
			target="_blank"
			rel="noopener noreferrer"
			className="cosmos-chat-link cosmos-chat-link-recommend group inline-flex items-center gap-1.5 align-baseline text-primary"
		>
			<span className="font-semibold">{children}</span>
			<ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
		</a>
	);
};

const baseMarkdownComponents: Components = {
	a: ({ href, children }) => {
		if (!href) {
			return <>{children}</>;
		}
		return (
			<a
				href={href}
				target="_blank"
				rel="noopener noreferrer"
				className="cosmos-chat-link text-primary"
			>
				{children}
			</a>
		);
	},
};

const defaultMarkdownComponents: Components = {
	...baseMarkdownComponents,
	ol: ({ children }) => <ol className="list-decimal pl-5 space-y-2 my-2">{children}</ol>,
	li: ({ children }) => <li className="leading-snug">{children}</li>,
	p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
};

const recommendMarkdownComponents: Components = {
	...baseMarkdownComponents,
	a: ({ href, children }) => <RecommendExternalLink href={href}>{children}</RecommendExternalLink>,
	strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
	h1: ({ children }) => <h1 className="text-lg font-semibold mt-1 mb-4">{children}</h1>,
	h2: ({ children }) => <h2 className="text-base font-semibold mt-6 mb-3 first:mt-0">{children}</h2>,
	h3: ({ children }) => (
		<h3 className="text-sm font-semibold mt-6 mb-3 first:mt-2 text-anchor">{children}</h3>
	),
	hr: () => (
		<div className="my-7 flex items-center gap-3" aria-hidden="true">
			<div className="h-px flex-1 bg-[var(--border)]" />
			<div className="h-1 w-1 rounded-full bg-[var(--border)]" />
			<div className="h-px flex-1 bg-[var(--border)]" />
		</div>
	),
	p: ({ children }) => <p className="mb-4 last:mb-0 leading-relaxed">{children}</p>,
	ol: ({ children }) => <ol className="list-decimal pl-5 space-y-5 my-4">{children}</ol>,
	ul: ({ children }) => <ul className="list-disc pl-5 space-y-3 my-4">{children}</ul>,
	li: ({ children }) => <li className="leading-relaxed [&>p]:mb-2 [&>p:last-child]:mb-0">{children}</li>,
};

export type AssistantMarkdownVariant = "default" | "recommend";

export function AssistantMarkdown({
	text,
	variant = "default",
}: {
	text: string;
	variant?: AssistantMarkdownVariant;
}) {
	const normalized =
		variant === "recommend" ? normalizeRecommendMarkdown(text) : normalizeAssistantMarkdown(text);
	const components =
		variant === "recommend" ? recommendMarkdownComponents : defaultMarkdownComponents;

	return (
		<ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
			{normalized}
		</ReactMarkdown>
	);
}
